import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import adminApi from '../services/adminApi';

const EMPTY_FORM = {
  slug: '', title: '', subtitle: '', tagline: '',
  frequency: '', format_type: '', color: '#3B5BA5',
  header_image: '', description: '', is_active: true, sort_order: 0,
};

const AdminPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null); // null | 'create' | 'edit'
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [alert, setAlert]       = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getPrograms()
      .then(({ data }) => setPrograms(data.data))
      .catch(() => setAlert({ type: 'error', msg: 'Impossible de charger les programmes.' }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setModal('form');
  };

  const openEdit = (p) => {
    setForm({
      slug: p.slug, title: p.title, subtitle: p.subtitle, tagline: p.tagline ?? '',
      frequency: p.frequency ?? '', format_type: p.format_type ?? '',
      color: p.color ?? '#3B5BA5', header_image: p.header_image ?? '',
      description: p.description, is_active: p.is_active, sort_order: p.sort_order ?? 0,
    });
    setEditing(p.id);
    setModal('form');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setAlert(null);
    try {
      if (editing) {
        await adminApi.updateProgram(editing, form);
        setAlert({ type: 'success', msg: 'Programme mis à jour.' });
      } else {
        await adminApi.createProgram(form);
        setAlert({ type: 'success', msg: 'Programme créé.' });
      }
      setModal(null);
      load();
    } catch (err) {
      const msgs = err.response?.data?.errors;
      const firstMsg = msgs ? Object.values(msgs)[0]?.[0] : err.response?.data?.message;
      setAlert({ type: 'error', msg: firstMsg || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce programme ? Cette action est irréversible.')) return;
    try {
      await adminApi.deleteProgram(id);
      setAlert({ type: 'success', msg: 'Programme supprimé.' });
      load();
    } catch {
      setAlert({ type: 'error', msg: 'Impossible de supprimer ce programme.' });
    }
  };

  const f = (field) => (e) => setForm({ ...form, [field]: field === 'is_active' ? e.target.checked : e.target.value });

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Programmes</h1>
        <p>Gérez les programmes de formation BE TALENTED</p>
      </div>

      {alert && (
        <div className={`admin-alert ${alert.type}`}>{alert.msg}</div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Liste des programmes ({programs.length})</h2>
          <button className="admin-btn primary" onClick={openCreate}>+ Nouveau programme</button>
        </div>

        {loading ? (
          <p style={{ color: '#aaa', textAlign: 'center', padding: 32 }}>Chargement…</p>
        ) : programs.length === 0 ? (
          <div className="admin-empty">
            <div className="empty-icon">🎓</div>
            <p>Aucun programme. Créez le premier !</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Titre</th>
                  <th>Sous-titre</th>
                  <th>Format</th>
                  <th>Fréquence</th>
                  <th>Couleur</th>
                  <th>Actif</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((p) => (
                  <tr key={p.id}>
                    <td>{p.sort_order}</td>
                    <td><strong>{p.title}</strong></td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.subtitle}</td>
                    <td>{p.format_type || '—'}</td>
                    <td>{p.frequency || '—'}</td>
                    <td>
                      <span className="color-swatch" style={{ background: p.color }} />
                      {p.color}
                    </td>
                    <td>
                      <span className={`admin-badge-active ${p.is_active ? 'yes' : 'no'}`}>
                        {p.is_active ? 'Oui' : 'Non'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="admin-btn secondary" onClick={() => openEdit(p)}>Modifier</button>
                        <button className="admin-btn danger" onClick={() => handleDelete(p.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'form' && (
        <div className="admin-form-overlay" onClick={() => setModal(null)}>
          <div className="admin-form-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? 'Modifier le programme' : 'Nouveau programme'}</h3>

            {alert?.type === 'error' && (
              <div className="admin-alert error">{alert.msg}</div>
            )}

            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label>Slug *</label>
                  <input value={form.slug} onChange={f('slug')} placeholder="ex: code" required />
                </div>
                <div className="form-group">
                  <label>Couleur</label>
                  <input type="color" value={form.color} onChange={f('color')} />
                </div>
              </div>

              <div className="form-group">
                <label>Titre *</label>
                <input value={form.title} onChange={f('title')} placeholder="ex: CODE" required />
              </div>

              <div className="form-group">
                <label>Sous-titre *</label>
                <input value={form.subtitle} onChange={f('subtitle')} placeholder="ex: Mindset & Performance" required />
              </div>

              <div className="form-group">
                <label>Tagline</label>
                <input value={form.tagline} onChange={f('tagline')} placeholder="Phrase d'accroche" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Fréquence</label>
                  <input value={form.frequency} onChange={f('frequency')} placeholder="ex: Tous les 2 mois" />
                </div>
                <div className="form-group">
                  <label>Format</label>
                  <input value={form.format_type} onChange={f('format_type')} placeholder="ex: En ligne" />
                </div>
              </div>

              <div className="form-group">
                <label>Image d'en-tête (chemin)</label>
                <input value={form.header_image} onChange={f('header_image')} placeholder="/assets/IMG/Header_Code.jpg" />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea value={form.description} onChange={f('description')} required rows={4} />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Ordre d'affichage</label>
                  <input type="number" min={0} value={form.sort_order} onChange={f('sort_order')} />
                </div>
                <div className="form-group" style={{ justifyContent: 'center' }}>
                  <label>
                    <input type="checkbox" checked={form.is_active} onChange={f('is_active')} style={{ marginRight: 8 }} />
                    Programme actif
                  </label>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setModal(null)}>Annuler</button>
                <button type="submit" className="admin-btn primary" disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPrograms;
